<template>
  <div style="background-color: #f8fafc; min-height: 100vh;" class="pb-12">
    <v-layout wrap justify-center class="mainfont">
      <v-snackbar v-model="showSnackBar" color="#1e293b" rounded="lg" top right>
        <v-layout align-center>
          <span style="color: white" class="font-weight-medium">
            {{ msg }}
          </span>
          <v-spacer></v-spacer>
          <v-btn small :ripple="false" icon @click="showSnackBar = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-layout>
      </v-snackbar>
      <v-snackbar v-model="autosaveSnackBar" data-tone="success" color="#1e293b" rounded="lg" :timeout="2500" bottom right>
        <v-icon small color="white" class="mr-2">mdi-cloud-check-outline</v-icon>
        <span style="color: white" class="font-weight-medium">Changes saved</span>
      </v-snackbar>
      <DraftLeaveDialog
        v-model="autosaveLeaveDialog"
        @save="autosaveLeaveSave"
        @discard="autosaveLeaveDiscard"
        @stay="autosaveLeaveStay"
      />

      <vue-element-loading
        :active="appLoading"
        :is-full-screen="true"
        background-color="#FFFFFF"
        color="#13736f"
        spinner="spinner"
      />

      <v-flex xs12 class="page-container bk-page">
        <!-- Camp header -->
        <section class="bk-hero">
          <safe-image class="bk-hero-img" :src="heroPhoto" :fallback="heroFallback" alt="" />
          <div class="bk-hero-shade"></div>
          <div class="bk-hero-content">
            <button type="button" class="bk-back" @click="$router.push('/mybooking')">
              <v-icon small color="white">mdi-arrow-left</v-icon>
              My Bookings
            </button>
            <div class="bk-hero-eyebrow">Application for online reservation &bull; Edit Mode</div>
            <h1 class="bk-hero-title">{{ campview.campName || (list.campid && list.campid.campName) || "Nature camp" }}</h1>
            <div class="bk-hero-tags">
              <span v-if="campview.district || dist" class="bk-tag">
                <v-icon x-small color="white">mdi-map-marker</v-icon>
                {{ campview.district || dist }}
              </span>
              <span class="bk-tag" :class="isFreeCamp ? 'bk-tag--free' : 'bk-tag--paid'">
                <v-icon x-small :color="isFreeCamp ? '#15803d' : '#b45309'">
                  {{ isFreeCamp ? "mdi-gift-outline" : "mdi-currency-inr" }}
                </v-icon>
                {{ isFreeCamp ? "Free camp" : "Paid camp" }}
              </span>
              <span v-if="campview.totalMembers" class="bk-tag">
                <v-icon x-small color="white">mdi-account-group-outline</v-icon>
                Max Participants {{ campview.totalMembers }} people
              </span>
              <span v-if="campview.divisionid && campview.divisionid.officeName" class="bk-tag">
                <v-icon x-small color="white">mdi-domain</v-icon>
                {{ campview.divisionid.officeName }}
              </span>
              <span class="bk-tag bk-tag--veg">
                Note: Vegetarian Food is provided in the camps
              </span>
            </div>
          </div>
        </section>

        <!-- Section progress -->
        <nav class="bk-steps" aria-label="Form sections">
          <button
            v-for="(st, i) in formSteps"
            :key="st.id"
            type="button"
            class="bk-step"
            :class="{ 'bk-step--done': st.done }"
            @click="scrollToSection(st.id)"
          >
            <span class="bk-step-num">
              <v-icon v-if="st.done" x-small color="white">mdi-check</v-icon>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="bk-step-label">{{ st.label }}</span>
          </button>
        </nav>

        <div class="bk-layout">
          <div class="bk-main">
            <!-- SECTION 1: Select Date and Duration -->
            <div id="bk-date" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-calendar-clock</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Select Date & Duration</h2>
                  <div class="bk-section-sub">When you want to visit and for how long.</div>
                </div>
              </div>

              <v-layout wrap>
                <v-flex xs12 sm6 md6 class="pa-2">
                  <label class="form-label-text">
                    Selected District <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    disabled
                    outlined
                    dense
                    hide-details="auto"
                    color="#13736f"
                    :items="districts"
                    v-model="dist"
                    item-text="name"
                    item-value="name"
                    item-color="#13736f"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md6 class="pa-2">
                  <label class="form-label-text">
                    Selected Camp <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    disabled
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    color="#13736f"
                    :value="campview.campName || (list.campid && list.campid.campName) || ''"
                    class="mainfont"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md4 class="pa-2">
                  <label class="form-label-text">
                    From Date <span class="required-star mr-1">*</span>
                  </label>
                  <DateField
                    v-model="cdate"
                    dense
                    outlined
                    hide-details="auto"
                    background-color="white"
                    :min="minDate"
                    :allowed-dates="filterAllowedDates"
                  >
                    <template v-slot:footer>
                      <div class="pa-2 px-3 text-center" style="background: #f8fafc; border-top: 1px solid #e2e8f0; font-size: 11.5px; color: #64748b;">
                        <v-icon x-small color="#13736f" class="mr-1">mdi-information-outline</v-icon>
                        Dates within 7 days are disabled.
                      </div>
                    </template>
                  </DateField>
                </v-flex>

                <v-flex xs12 sm6 md4 class="pa-2">
                  <label class="form-label-text">
                    To Date <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    dense
                    outlined
                    readonly
                    tabindex="-1"
                    hide-details="auto"
                    :value="formattedEndDate"
                    background-color="#f8fafc"
                    placeholder="Auto-calculated from duration"
                    color="#13736f"
                    class="mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-calendar-check</v-icon>
                    </template>
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md4 class="pa-2">
                  <label class="form-label-text">
                    Duration of Camp <span class="required-star">*</span>
                  </label>
                  <v-select
                    outlined
                    dense
                    hide-details="auto"
                    color="#13736f"
                    :items="campDurationOptions"
                    v-model="list.campDuration"
                    item-text="text"
                    item-value="value"
                    item-color="#13736f"
                    placeholder="Select duration"
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-select>
                </v-flex>
              </v-layout>
            </div>

            <!-- Slots Unavailable Alert -->
            <div v-if="checkstatus === 0" class="pa-6 text-center rounded-lg mb-6" style="background-color: #fef2f2; border: 1px solid #fecaca;">
              <v-icon color="#dc2626" size="32" class="mb-2">mdi-alert-circle-outline</v-icon>
              <div style="font-size: 18px; color: #dc2626; font-weight: 700;">No Slots Available for Selected Date!</div>
              <div style="font-size: 14px; color: #991b1b;" class="mt-1">Please select an alternative date or camp duration.</div>
            </div>

            <!-- SECTION 2: Applicant Details -->
            <div id="bk-applicant" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-card-account-details-outline</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Applicant Details</h2>
                  <div class="bk-section-sub">The institution or organisation applying.</div>
                </div>
              </div>

              <v-layout wrap>
                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Applicant Category <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    outlined
                    dense
                    color="#13736f"
                    :items="applicantcategorys"
                    v-model="list.applicantCategory"
                    item-text="name"
                    item-value="name"
                    item-color="#13736f"
                    hide-details="auto"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2" v-if="list.applicantCategory === 'Students'">
                  <label class="form-label-text">
                    Student Category <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    outlined
                    dense
                    color="#13736f"
                    :items="studentCategories"
                    v-model="list.studentCategory"
                    item-color="#13736f"
                    hide-details="auto"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2" v-if="list.applicantCategory === 'Students'">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div>
                        <label class="form-label-text">
                          Inst/Org Registration Code <span class="required-star">*</span>
                        </label>
                        <v-text-field
                          dense
                          color="#13736f"
                          outlined
                          background-color="white"
                          v-model="list.instCode"
                          hide-details="auto"
                          v-on="on"
                          @input="validateInstCode"
                          placeholder="Reg. No."
                        ></v-text-field>
                      </div>
                    </template>
                    <span>Registration number of your Institution/Organisation</span>
                  </v-tooltip>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Name of Inst/Organisation <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    type="text"
                    dense
                    color="#13736f"
                    outlined
                    background-color="white"
                    :rules="appRules.orgName"
                    v-model="list.instName"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Type of Inst/Organisation <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    outlined
                    dense
                    color="#13736f"
                    :items="institutionTypes"
                    v-model="list.instType"
                    item-text="name"
                    item-value="name"
                    item-color="#13736f"
                    hide-details="auto"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Address of Inst/Organisation <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    dense
                    color="#13736f"
                    outlined
                    background-color="white"
                    :rules="appRules.orgAddress"
                    v-model="list.instAddress"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Mobile Number <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    type="number"
                    :rules="mobNumberRules"
                    dense
                    color="#13736f"
                    outlined
                    class="inputPrice"
                    background-color="white"
                    v-model="list.instMobileNo"
                    hide-details="auto"
                    placeholder="10-digit Mobile"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Phone Number (Landline) <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    type="number"
                    dense
                    clearable
                    outlined
                    class="inputPrice"
                    color="#13736f"
                    background-color="white"
                    :rules="appRules.landline"
                    v-model="list.instTeleNo"
                    hide-details="auto"
                    placeholder="11-digit Landline"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Institution Email <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    :rules="emailRules"
                    dense
                    color="#13736f"
                    outlined
                    background-color="white"
                    v-model="list.instEmail"
                    hide-details="auto"
                    placeholder="email@domain.com"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Institution District <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    dense
                    outlined
                    color="#13736f"
                    :items="districts"
                    v-model="list.instDistrict"
                    background-color="white"
                    hide-details="auto"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>
              </v-layout>
            </div>

            <!-- SECTION 3: Head of Institution Details (Only for Students) -->
            <div id="bk-head" class="bk-section" v-if="list.applicantCategory === 'Students'">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-account-tie</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Details of Head of Institution / Organization</h2>
                  <div class="bk-section-sub">Required for student and organizational groups.</div>
                </div>
              </div>

              <v-layout wrap>
                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Name of Head of Inst/Org <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    dense
                    color="#13736f"
                    outlined
                    type="text"
                    background-color="white"
                    :rules="appRules.personName"
                    v-model="list.instHeadName"
                    @input="filterNonNumericInput"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    Designation <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    dense
                    color="#13736f"
                    outlined
                    background-color="white"
                    :rules="appRules.designation"
                    v-model="list.headDesignation"
                    @input="filterNonNumericInput"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2">
                  <label class="form-label-text">
                    ID Card Type <span class="required-star">*</span>
                  </label>
                  <v-autocomplete
                    outlined
                    dense
                    color="#13736f"
                    :items="idcardtypes"
                    v-model="list.idCardType"
                    item-text="name"
                    item-value="name"
                    item-color="#13736f"
                    hide-details="auto"
                    clearable
                    class="custom-autocomplete mainfont"
                  >
                    <template v-slot:append>
                      <v-icon color="#64748b">mdi-chevron-down</v-icon>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md3 class="pa-2" v-if="list.idCardType">
                  <label class="form-label-text">
                    {{ list.idCardType === 'Aadhaar Card' ? 'Aadhaar Number' : 'ID Card Number' }} <span class="required-star">*</span>
                  </label>
                  <v-text-field
                    :type="list.idCardType === 'Aadhaar Card' ? 'number' : 'text'"
                    dense
                    color="#13736f"
                    outlined
                    :class="{ inputPrice: list.idCardType === 'Aadhaar Card' }"
                    background-color="white"
                    v-model="list.idNo"
                    :rules="list.idCardType === 'Aadhaar Card' ? aadharNumberRules : []"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </div>

            <!-- SECTION 4: Group Leader / Teaching Staff Details -->
            <div id="bk-leaders" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-account-group-outline</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Group Leader / Teaching Staff Details</h2>
                  <div class="bk-section-sub">Who will lead the group at the camp.</div>
                </div>
                <v-btn
                  depressed
                  class="btn-act btn-act--sm btn-act--primary bk-head-action"
                  @click="addLeaderDetails()"
                >
                  <v-icon left small>mdi-plus-circle</v-icon> Add Leader
                </v-btn>
              </div>

              <div v-for="(item, i) in leaderDetails2" :key="'leader-' + i" class="bk-repeater mb-4 pa-4">
                <div class="bk-repeater-head">
                  <span class="bk-repeater-title">
                    <span class="bk-repeater-num">{{ i + 1 }}</span>
                    Group leader {{ i + 1 }}
                  </span>
                  <v-btn
                    v-if="leaderDetails2.length > 1"
                    x-small
                    color="#ef4444"
                    text
                    class="font-weight-bold text-none"
                    @click="removeLeaderDetail(i)"
                  >
                    <v-icon left x-small>mdi-delete-outline</v-icon> Remove
                  </v-btn>
                </div>

                <v-layout wrap>
                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Name <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="appRules.personName"
                      v-model="item.leaderName"
                      dense
                      outlined
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Designation <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="appRules.designation"
                      v-model="item.leaderAddress1"
                      dense
                      outlined
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Address <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="appRules.address"
                      v-model="item.leaderAddress2"
                      dense
                      outlined
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Telephone <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="appRules.telephone"
                      v-model="item.telephone"
                      type="tel"
                      maxlength="10"
                      counter="10"
                      dense
                      outlined
                      class="inputPrice"
                      clearable
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Mobile <span class="required-star">*</span></label>
                    <v-text-field
                      :rules="mobNumberRules"
                      v-model="item.leaderPhone"
                      type="number"
                      dense
                      outlined
                      clearable
                      class="inputPrice"
                      color="#13736f"
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Email <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="emailRules"
                      v-model="item.leaderEmail"
                      dense
                      outlined
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6 md3 class="pa-2">
                    <label class="form-label-text">Gender <span class="required-star">*</span></label>
                    <v-autocomplete
                      outlined
                      dense
                      color="#13736f"
                      :items="gender"
                      v-model="item.leaderGender"
                      item-text="name"
                      item-value="name"
                      item-color="#13736f"
                      clearable
                      hide-details="auto"
                      class="custom-autocomplete mainfont"
                      background-color="white"
                    >
                      <template v-slot:append>
                        <v-icon color="#64748b">mdi-chevron-down</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-flex>
                </v-layout>
              </div>
            </div>

            <!-- SECTION 5: Details of Participants -->
            <div id="bk-participants" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-account-multiple-outline</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Details of Participants</h2>
                  <div class="bk-section-sub">Everyone attending the camp.</div>
                </div>
                <v-btn
                  depressed
                  class="btn-act btn-act--sm btn-act--sky bk-head-action"
                  title="Download an Excel sheet to fill in the participants"
                  @click="downloadParticipantTemplate()"
                >
                  <v-icon left small>mdi-file-download-outline</v-icon> Excel template
                </v-btn>
                <v-btn
                  depressed
                  class="btn-act btn-act--sm btn-act--indigo bk-head-action"
                  :disabled="memberCapReached(memberDetails2.length)"
                  title="Fill the participants from an Excel (.xlsx) sheet"
                  @click="chooseParticipantExcel()"
                >
                  <v-icon left small>mdi-file-upload-outline</v-icon> Upload Excel
                </v-btn>
                <input ref="participantExcel" type="file" accept=".xlsx" style="display: none" @change="onParticipantExcel" />
                <v-btn
                  depressed
                  class="btn-act btn-act--sm btn-act--primary bk-head-action"
                  :disabled="memberCapReached(memberDetails2.length)"
                  @click="addValues()"
                >
                  <v-icon left small>mdi-plus-circle</v-icon> Add Participant
                </v-btn>
              </div>

              <div
                v-if="memberLimit"
                class="bk-limit"
                :class="{ 'bk-limit--extra': memberDetails2.length > memberLimit, 'bk-limit--full': memberCapReached(memberDetails2.length) }"
              >
                <v-icon small color="currentColor">mdi-information-outline</v-icon>
                <span>
                  This camp takes up to <strong>{{ memberLimit }}</strong> participants. If more are coming, up to
                  <strong>{{ extraMembers }}</strong> additional names can be added (<strong>{{ memberHardLimit }}</strong> in all).
                  <strong>{{ memberDetails2.length }}</strong> entered<template v-if="memberDetails2.length > memberLimit">,
                    {{ memberDetails2.length - memberLimit }} of {{ extraMembers }} additional names used</template><template
                    v-if="memberCapReached(memberDetails2.length)"> &mdash; no more can be added</template>.
                </span>
              </div>

              <div v-for="(item, i) in memberDetails2" :key="'member-' + i" class="bk-repeater bk-repeater--member mb-3 pa-3 px-4">
                <div class="bk-repeater-head">
                  <span class="bk-repeater-title">
                    <span class="bk-repeater-num">{{ i + 1 }}</span>
                    Participant No. {{ i + 1 }}
                    <span v-if="memberLimit && i >= memberLimit" class="bk-extra-tag">Additional</span>
                    <span v-if="item.isFromLeader" class="ml-2 font-weight-regular" style="color: #64748b; font-size: 12px;">
                      (Group Leader)
                    </span>
                  </span>
                  <v-btn
                    v-if="!item.isFromLeader"
                    x-small
                    color="#ef4444"
                    text
                    class="font-weight-bold text-none"
                    @click="removeMember(i)"
                    title="Remove Participant"
                  >
                    <v-icon left x-small>mdi-delete-outline</v-icon> Remove
                  </v-btn>
                </div>

                <v-layout wrap align-center>
                  <v-flex xs12 sm4 class="pa-2">
                    <label class="form-label-text">Name <span class="required-star">*</span></label>
                    <v-text-field
                      color="#13736f"
                      :rules="appRules.personName"
                      v-model="item.memberName"
                      dense
                      outlined
                      :disabled="item.isFromLeader"
                      hide-details="auto"
                      background-color="white"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm4 class="pa-2">
                    <label class="form-label-text">Date of Birth <span class="required-star">*</span></label>
                    <DateField
                      v-model="item.memberDob"
                      dense
                      outlined
                      hide-details="auto"
                      background-color="white"
                      :max="calculateMaxDate()"
                    />
                  </v-flex>

                  <v-flex xs12 sm4 class="pa-2">
                    <label class="form-label-text">Gender <span class="required-star">*</span></label>
                    <v-autocomplete
                      outlined
                      dense
                      color="#13736f"
                      :items="gender"
                      v-model="item.memberGender"
                      :disabled="item.isFromLeader"
                      item-text="name"
                      item-value="name"
                      item-color="#13736f"
                      clearable
                      hide-details="auto"
                      class="custom-autocomplete mainfont"
                      background-color="white"
                    >
                      <template v-slot:append>
                        <v-icon color="#64748b">mdi-chevron-down</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-flex>
                </v-layout>
              </div>

              <!-- Participant summary stats -->
              <div class="bk-stats">
                <div class="bk-stat bk-stat--total">
                  <v-icon color="#13736f">mdi-account-multiple-outline</v-icon>
                  <div>
                    <div class="bk-stat-value">{{ memberDetails2.length }}</div>
                    <div class="bk-stat-label">Total participants</div>
                  </div>
                </div>
                <div class="bk-stat bk-stat--male">
                  <v-icon color="#2563eb">mdi-gender-male</v-icon>
                  <div>
                    <div class="bk-stat-value">{{ calculatedGenderCounts2.male }}</div>
                    <div class="bk-stat-label">No. of Males</div>
                  </div>
                </div>
                <div class="bk-stat bk-stat--female">
                  <v-icon color="#db2777">mdi-gender-female</v-icon>
                  <div>
                    <div class="bk-stat-value">{{ calculatedGenderCounts2.female }}</div>
                    <div class="bk-stat-label">No. of Females</div>
                  </div>
                </div>
                <div class="bk-stat bk-stat--trans">
                  <v-icon color="#7c3aed">mdi-gender-transgender</v-icon>
                  <div>
                    <div class="bk-stat-value">{{ calculatedGenderCounts2.transgender }}</div>
                    <div class="bk-stat-label">No. of Transgenders</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SECTION 6: Scanned Documents -->
            <div id="bk-docs" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-file-document-multiple-outline</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Upload Documents</h2>
                  <div class="bk-section-sub">PDF, JPEG or PNG, up to 100 KB each.</div>
                </div>
                <span class="bk-head-note">
                  <v-icon x-small color="#b45309">mdi-information-outline</v-icon>
                  Max 100 KB
                </span>
              </div>

              <v-layout wrap>
                <v-flex xs12 md6 class="pa-2" v-if="list.applicantCategory === 'Students'">
                  <label class="bk-file" :class="{ 'bk-file--done': hasFile(idProof) }" for="idProof">
                    <span class="bk-file-icon">
                      <v-icon>{{ hasFile(idProof) ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
                    </span>
                    <span class="bk-file-text">
                      <span class="bk-file-title">ID proof of Head of Inst/Org <span class="required-star">*</span></span>
                      <span class="bk-file-name">{{ getFileName(idProof) }}</span>
                    </span>
                    <button
                      v-if="hasFile(idProof)"
                      type="button"
                      class="bk-file-view-btn mr-2"
                      title="Open this document in a new tab"
                      @click.prevent.stop="viewDocument('idProof')"
                    >
                      <v-icon x-small color="#13736f" class="mr-1">mdi-eye-outline</v-icon> View
                    </button>
                    <span class="bk-file-btn">{{ hasFile(idProof) ? 'Change' : 'Browse' }}</span>
                    <input
                      type="file"
                      id="idProof"
                      ref="idProof"
                      accept=".pdf, .jpeg, .png"
                      class="bk-file-input"
                      @change="handleFileChange('idProof')"
                    />
                  </label>
                </v-flex>

                <v-flex xs12 md6 class="pa-2">
                  <label class="bk-file" :class="{ 'bk-file--done': hasFile(reqLetter) }" for="reqLetter">
                    <span class="bk-file-icon">
                      <v-icon>{{ hasFile(reqLetter) ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
                    </span>
                    <span class="bk-file-text">
                      <span class="bk-file-title">Request Letter from Head of Inst/Org <span class="required-star">*</span></span>
                      <span class="bk-file-name">{{ getFileName(reqLetter) }}</span>
                    </span>
                    <button
                      v-if="hasFile(reqLetter)"
                      type="button"
                      class="bk-file-view-btn mr-2"
                      title="Open this document in a new tab"
                      @click.prevent.stop="viewDocument('reqLetter')"
                    >
                      <v-icon x-small color="#13736f" class="mr-1">mdi-eye-outline</v-icon> View
                    </button>
                    <span class="bk-file-btn">{{ hasFile(reqLetter) ? 'Change' : 'Browse' }}</span>
                    <input
                      type="file"
                      id="reqLetter"
                      ref="reqLetter"
                      accept=".pdf, .jpeg, .png"
                      class="bk-file-input"
                      @change="handleFileChange('reqLetter')"
                    />
                  </label>
                </v-flex>

                <v-flex xs12 md6 class="pa-2">
                  <label class="bk-file" :class="{ 'bk-file--done': hasFile(participantfile) }" for="participantfile">
                    <span class="bk-file-icon">
                      <v-icon>{{ hasFile(participantfile) ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
                    </span>
                    <span class="bk-file-text">
                      <span class="bk-file-title">List of participants signed by Head <span class="required-star">*</span></span>
                      <span class="bk-file-name">{{ getFileName(participantfile) }}</span>
                    </span>
                    <button
                      v-if="hasFile(participantfile)"
                      type="button"
                      class="bk-file-view-btn mr-2"
                      title="Open this document in a new tab"
                      @click.prevent.stop="viewDocument('participantfile')"
                    >
                      <v-icon x-small color="#13736f" class="mr-1">mdi-eye-outline</v-icon> View
                    </button>
                    <span class="bk-file-btn">{{ hasFile(participantfile) ? 'Change' : 'Browse' }}</span>
                    <input
                      type="file"
                      id="participantfile"
                      ref="participantfile"
                      accept=".pdf, .jpeg, .png"
                      class="bk-file-input"
                      @change="handleFileChange('participantfile')"
                    />
                  </label>
                </v-flex>

                <v-flex xs12 md6 class="pa-2">
                  <label class="bk-file" :class="{ 'bk-file--done': hasFile(leaderIdProof) }" for="leaderIdProof">
                    <span class="bk-file-icon">
                      <v-icon>{{ hasFile(leaderIdProof) ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
                    </span>
                    <span class="bk-file-text">
                      <span class="bk-file-title">ID Proof of Group Leader / Teaching Staff <span class="required-star">*</span></span>
                      <span class="bk-file-name">{{ getFileName(leaderIdProof) }}</span>
                    </span>
                    <button
                      v-if="hasFile(leaderIdProof)"
                      type="button"
                      class="bk-file-view-btn mr-2"
                      title="Open this document in a new tab"
                      @click.prevent.stop="viewDocument('leaderIdProof')"
                    >
                      <v-icon x-small color="#13736f" class="mr-1">mdi-eye-outline</v-icon> View
                    </button>
                    <span class="bk-file-btn">{{ hasFile(leaderIdProof) ? 'Change' : 'Browse' }}</span>
                    <input
                      type="file"
                      id="leaderIdProof"
                      ref="leaderIdProof"
                      accept=".pdf, .jpeg, .png"
                      class="bk-file-input"
                      @change="handleFileChange('leaderIdProof')"
                    />
                  </label>
                </v-flex>
              </v-layout>
            </div>

            <!-- SECTION 7: Undertaking & Declaration -->
            <div id="bk-declare" class="bk-section">
              <div class="bk-section-head">
                <span class="bk-section-icon"><v-icon color="#13736f">mdi-shield-check-outline</v-icon></span>
                <div class="bk-section-heading">
                  <h2 class="bk-section-title">Undertaking & Declaration</h2>
                  <div class="bk-section-sub">Read and accept the camp rules.</div>
                </div>
              </div>

              <v-card flat class="bk-terms mb-4">
                <div v-for="(content, index) in agreementContent" :key="'agree-' + index" class="d-flex mb-3 text-body-2" style="color: #475569; line-height: 1.5;">
                  <v-icon small color="#13736f" class="mr-2 align-self-start mt-1">mdi-check-circle-outline</v-icon>
                  <span>{{ content }}</span>
                </div>
              </v-card>

              <div class="bk-consent" :class="{ 'bk-consent--on': isChecked }">
                <v-checkbox v-model="isChecked" color="#13736f" hide-details class="ma-0 pa-0 mr-3"></v-checkbox>
                <span class="bk-consent-text">
                  I hereby confirm that I have read, understood, and accept all the terms and conditions outlined above.
                </span>
              </div>

              <v-layout wrap class="mt-4">
                <v-flex xs12 sm6 md4 class="pa-2">
                  <label class="form-label-text">Name of Applicant</label>
                  <v-text-field
                    type="text"
                    dense
                    disabled
                    color="#13736f"
                    outlined
                    background-color="white"
                    :rules="appRules.applicantName"
                    v-model="list.applicantname"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md4 class="pa-2" v-if="!isFreeCamp">
                  <label class="form-label-text">Amount to be paid</label>
                  <v-text-field
                    type="text"
                    dense
                    disabled
                    color="#13736f"
                    outlined
                    background-color="white"
                    :value="'₹ ' + (calculatedTotalAmount || 0)"
                    hide-details="auto"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </div>

            <!-- Date reservation after saving -->
            <div v-if="holdUntil" class="bk-hold" :class="{ 'bk-hold--expired': holdExpired }" role="status">
              <v-icon small :color="holdExpired ? '#b45309' : '#13736f'">
                {{ holdExpired ? "mdi-timer-off-outline" : "mdi-timer-sand" }}
              </v-icon>
              <div>
                <div v-if="holdActive">
                  <strong>These dates are reserved for you for 10 minutes.</strong>
                  Submit before {{ holdUntilTime }} ({{ holdTimeLeft }} left) to keep them. After that, other applicants can book them.
                </div>
                <div v-else>
                  <strong>Your 10-minute reservation has ended.</strong>
                  <template v-if="holdLocked">
                    You have used all {{ holdsMax }} reservations for these dates and can reserve them again after {{ holdLockedUntilText }}.
                  </template>
                  <template v-else>
                    Save again to check the dates are still free and reserve them for another 10 minutes.
                  </template>
                </div>
                <div v-if="holdInfo" class="bk-hold-count">Reservation {{ holdsUsed }} of {{ holdsMax }} for these dates</div>
              </div>
            </div>

            <!-- Actions Bar -->
            <div class="bk-actions">
              <div class="bk-actions-hint">
                <v-icon small color="#13736f">{{ isSaved ? "mdi-check-circle-outline" : "mdi-information-outline" }}</v-icon>
                {{ isSaved ? "Changes saved. Preview PDF, or submit application." : "Accept the declaration and save changes, then submit." }}
              </div>
              <div class="bk-actions-buttons">
                <v-btn
                  @click="editApplication()"
                  depressed
                  class="btn-act btn-act--primary"
                  :disabled="!isChecked"
                >
                  <v-icon left>mdi-content-save-outline</v-icon> Save Changes
                </v-btn>
                <v-btn @click="getPreview()" depressed class="btn-act btn-act--info">
                  <v-icon left>mdi-file-eye-outline</v-icon> Preview PDF
                </v-btn>
                <v-btn
                  v-if="isSaved"
                  @click="submit()"
                  depressed
                  class="btn-act btn-act--success"
                  :disabled="!isChecked || submitted"
                >
                  <v-icon left>mdi-send-outline</v-icon> Submit
                </v-btn>
              </div>
            </div>

          </div>

          <!-- Booking summary (Sticky sidebar) -->
          <aside class="bk-aside">
            <div class="bk-summary">
              <div class="bk-summary-title">Booking summary</div>
              <div class="bk-progress" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
                <div class="bk-progress-bar" :style="{ width: progress + '%' }"></div>
              </div>
              <div class="bk-progress-text">{{ doneSteps }} of {{ formSteps.length }} sections complete</div>
              <div v-if="autosaveSavedAt" class="bk-autosave-note">
                <v-icon x-small color="#13736f">mdi-cloud-check-outline</v-icon>
                Draft auto-saved at {{ autosaveSavedTime }}
              </div>

              <div class="bk-sum-counts">
                <div class="bk-sum-count bk-sum-count--total">
                  <v-icon color="#13736f">mdi-account-multiple-outline</v-icon>
                  <div>
                    <div class="bk-sum-count-value">{{ memberDetails2.length }}<small v-if="campview.totalMembers"> of {{ campview.totalMembers }}</small></div>
                    <div class="bk-sum-count-label">Total participants</div>
                  </div>
                </div>
                <div class="bk-sum-count bk-sum-count--leader">
                  <v-icon color="#15803d">mdi-account-tie-outline</v-icon>
                  <div>
                    <div class="bk-sum-count-value">{{ leaderDetails2.length }}</div>
                    <div class="bk-sum-count-label">Group leaders</div>
                  </div>
                </div>
                <div class="bk-sum-count bk-sum-count--male">
                  <v-icon color="#2563eb">mdi-gender-male</v-icon>
                  <div>
                    <div class="bk-sum-count-value">{{ calculatedGenderCounts2.male }}</div>
                    <div class="bk-sum-count-label">No. of Males</div>
                  </div>
                </div>
                <div class="bk-sum-count bk-sum-count--female">
                  <v-icon color="#db2777">mdi-gender-female</v-icon>
                  <div>
                    <div class="bk-sum-count-value">{{ calculatedGenderCounts2.female }}</div>
                    <div class="bk-sum-count-label">No. of Females</div>
                  </div>
                </div>
                <div class="bk-sum-count bk-sum-count--trans">
                  <v-icon color="#7c3aed">mdi-gender-transgender</v-icon>
                  <div>
                    <div class="bk-sum-count-value">{{ calculatedGenderCounts2.transgender }}</div>
                    <div class="bk-sum-count-label">No. of Transgenders</div>
                  </div>
                </div>
              </div>

              <dl class="bk-summary-list">
                <div>
                  <dt>Date</dt>
                  <dd>
                    <template v-if="formattedStartDate && formattedEndDate">
                      {{ formattedStartDate }} to {{ formattedEndDate }}
                    </template>
                    <template v-else-if="formattedStartDate">
                      {{ formattedStartDate }}
                    </template>
                    <template v-else>
                      Not selected
                    </template>
                  </dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{{ list.campDuration ? list.campDuration + (list.campDuration == 1 ? " day" : " days") : "Not selected" }}</dd>
                </div>
                <div>
                  <dt>Category</dt>
                  <dd>
                    <template v-if="list.applicantCategory === 'Students' && list.studentCategory">
                      {{ list.applicantCategory }} ({{ list.studentCategory }})
                    </template>
                    <template v-else>
                      {{ list.applicantCategory || "Not selected" }}
                    </template>
                  </dd>
                </div>
                <div>
                  <dt>Applicant</dt>
                  <dd>{{ list.applicantname || "Not provided" }}</dd>
                </div>
                <div>
                  <dt>Institution</dt>
                  <dd>{{ list.instName || "Not provided" }}</dd>
                </div>
                <div v-if="list.applicantCategory === 'Students'">
                  <dt>Institution type</dt>
                  <dd>{{ list.instType || "Not provided" }}</dd>
                </div>
                <div>
                  <dt>District</dt>
                  <dd>{{ list.instDistrict || "Not provided" }}</dd>
                </div>
                <div>
                  <dt>Mobile</dt>
                  <dd>{{ list.instMobileNo || "Not provided" }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ list.instEmail || "Not provided" }}</dd>
                </div>
              </dl>

              <!-- <div class="bk-fee" :class="{ 'bk-fee--free': isFreeCamp }">
                <span>{{ isFreeCamp ? "Camp fee" : "Estimated fee" }}</span>
                <strong>{{ feeLabel }}</strong>
              </div>
              <p v-if="!isFreeCamp" class="bk-fee-note">
                You pay only after your application is approved.
              </p> -->
            </div>
          </aside>
        </div>

        <!-- Payment Details Modal Dialog -->
        <v-dialog
          :retain-focus="true"
          persistent
          v-model="submitdialog"
          max-width="700px"
        >
          <v-card class="rounded-lg overflow-hidden">
            <div class="dialog-head">
              <div>
                <div class="dialog-head-title">Payment details</div>
                <div class="dialog-head-subtitle">Used for the e-Treasury payment receipt.</div>
              </div>
            </div>
            
            <v-card-text class="pa-6">
              <v-alert color="#f0fdf4" border="left" colored-border icon="mdi-currency-inr" class="mb-4">
                <span class="font-weight-bold" style="color: #166534; font-size: 16px;">
                  Total Amount To Be Paid: ₹ {{ calculatedTotalAmount }}/-
                </span>
              </v-alert>

              <v-layout wrap>
                <v-flex xs12 sm6 class="pa-2">
                  <label class="form-label-text">Name <span class="required-star">*</span></label>
                  <v-text-field
                    type="text"
                    dense
                    clearable
                    outlined
                    color="#13736f"
                    v-model="paymentname"
                    hide-details="auto"
                    :rules="[(v) => !!v || 'Name is required']"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 class="pa-2">
                  <label class="form-label-text">Address <span class="required-star">*</span></label>
                  <v-text-field
                    type="text"
                    dense
                    clearable
                    outlined
                    color="#13736f"
                    v-model="paymentaddress"
                    hide-details="auto"
                    :rules="[(v) => !!v || 'Address is required']"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 class="pa-2">
                  <label class="form-label-text">Phone Number <span class="required-star">*</span></label>
                  <v-text-field
                    type="number"
                    dense
                    clearable
                    outlined
                    class="inputPrice"
                    color="#13736f"
                    v-model="paymentphonenumber"
                    hide-details="auto"
                    :rules="[
                      (v) => !!v || 'Phone number is required',
                      (v) => /^[0-9]{10}$/.test(v) || 'Must be 10 digits',
                    ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 class="pa-2">
                  <label class="form-label-text">Pincode <span class="required-star">*</span></label>
                  <v-text-field
                    type="number"
                    dense
                    clearable
                    outlined
                    class="inputPrice"
                    color="#13736f"
                    v-model="paymentpincode"
                    hide-details="auto"
                    :rules="[
                      (v) => !!v || 'Pincode is required',
                      (v) => /^[0-9]{6}$/.test(v) || 'Must be 6 digits',
                    ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 class="pa-2">
                  <label class="form-label-text">PAN Card Number <span class="required-star">*</span></label>
                  <v-text-field
                    type="text"
                    dense
                    clearable
                    outlined
                    color="#13736f"
                    v-model="paymentpancardnumber"
                    hide-details="auto"
                    :rules="[
                      (v) => !!v || 'PAN Card Number is required',
                      (v) => /^[A-Za-z0-9]{10}$/.test(v) || 'Must be 10 characters',
                    ]"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="px-6 py-3" style="background-color: #fafbfc;">
              <v-spacer></v-spacer>
              <v-btn depressed class="btn-act btn-act--neutral" @click="paymentdialog()">
                Cancel
              </v-btn>
              <v-btn
                depressed
                class="btn-act btn-act--success"
                :disabled="submitted"
                @click="submit()"
              >
                {{ submitted ? "Processing..." : "Proceed For Payment" }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- No slots available dialog -->
        <v-dialog :retain-focus="true" persistent v-model="memberdialog" max-width="400px">
          <v-card class="rounded-lg text-center pa-5">
            <v-icon color="#ef4444" size="48" class="mb-3">mdi-alert-circle-outline</v-icon>
            <div class="mainfont font-weight-medium mb-2" style="color: #1e293b; font-size: 16px;">
              Please choose another date
            </div>
            <div class="mainfont mb-4" style="color: #475569; font-size: 14px;">
              {{ dateMsg }}
            </div>
            <v-btn depressed class="btn-act btn-act--primary px-8" @click="submit2()">
              OK
            </v-btn>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { campPhoto } from "@/utils/campPhotos";
import SafeImage from "@/components/Common/SafeImage";
import applicationRulesMixin from "@/components/Common/applicationRulesMixin";
import participantExcelMixin from "@/components/Common/participantExcelMixin";
import documentViewMixin from "@/components/Common/documentViewMixin";
import draftAutosaveMixin from "@/components/Common/draftAutosaveMixin";
import DraftLeaveDialog from "@/components/Common/DraftLeaveDialog";
import dateHoldMixin from "@/components/Common/dateHoldMixin";

export default {
  name: "EditMyBookings",
  components: { SafeImage, DraftLeaveDialog },
  mixins: [draftAutosaveMixin, dateHoldMixin, applicationRulesMixin, participantExcelMixin, documentViewMixin],
  data() {
    return {
      allowedFileTypes: ["application/pdf", "image/jpeg", "image/png"],
      idProof: null,
      isSaved: false,
      reqLetter: null,
      participantfile: null,
      leaderIdProof: null,
      dist: this.$route.query.district,
      cdate: null,
      // dateKey of the last date/duration that passed the availability check
      availableDateKey: null,
      dateMsg: "",
      gender: ["Male", "Female", "Transgender"],
      memberDetails2: [],
      leaderDetails2: [],
      // Auxiliary lists for backwards compatibility if needed
      memberDetails: [],
      leaderDetails: [],
      memberdialog: false,
      submitdialog: false,
      submitted: false,
      checkstatus: null,

      paymentphonenumber: "",
      paymentpancardnumber: "",
      paymentname: "",
      paymenttaxid: "",
      paymentpincode: "",
      paymentaddress: "",

      idcardtypes: ["Election Id Card", "Driving License", "Aadhaar Card", "Government ID card"],
      institutionTypes: ["Government", "Aided", "Private", "Others"],
      districts: [
        "Thiruvananthapuram",
        "Kollam",
        "Pathanamthitta",
        "Alappuzha",
        "Kottayam",
        "Idukki",
        "Ernakulam",
        "Thrissur",
        "Palakkad",
        "Malappuram",
        "Kozhikode",
        "Wayanad",
        "Kannur",
        "Kasaragod",
      ],
      campDurationOptions: [
        { text: "1 Day", value: "1" },
        { text: "2 Days", value: "2" },
        { text: "3 Days", value: "3" },
      ],
      applicantcategorys: [
        "Students",
        "Media Personnel",
        "Corporation Councilors,Muncipal Councilors,Jilla Panchayat Members,Block Panchayat Members And Grama Panchayt Members",
        "Members Of Charitable, Non-governmental and Youth Organizations",
      ],
      studentCategories: [
        "Upper Primary",
        "High School",
        "Higher Secondary",
        "College/University",
      ],

      showSnackBar: false,
      isChecked: false,
      appLoading: false,
      msg: "",
      campview: {},
      campidx: null,
      // Dates already taken or blocked for this camp (YYYY-MM-DD), disabled in the From Date picker.
      Reserveddates1: [],
      amounttobepaid: 0,
      list: {},
      getdetailsdata: "",
      menu1: false,

      agreementContent: [
        "I, hereby undertake on behalf of the Institution/Organization/Team that myself and all the Team members taking part in the Nature Camp shall abide by the conditions as per the existing laws pertaining to Forests & Wildlife, orders of the Forest Officers/competent authorities during the Nature Camp and maintain discipline throughout the course of the Nature Camp.",
        "I, hereby declare that the entries made by me, for the members of the team, in the Application Form are complete and true to the best of my knowledge, belief, and information.",
        "I, hereby undertake to present the original documents/ID proof (of Group leader) for verification immediately upon demand by the competent authorities, as and when required.",
        "I, hereby declare that I and or my team/group members shall be solely responsible for our involvement in any kind of unlawful activities whether inside or outside the premises at the selected location of the Nature Camp, and shall be liable for punishment, if any, as per the law of the land.",
        "I further declare that we are aware that the permission given to me and or my team may be canceled, at any stage, if I and or my team members are found ineligible and/or the information provided by me are found to be incorrect.",
        "I declare that my team members and I will strictly adhere to the code of conduct, as issued by the Competent Authority for the Nature Camp Programme at the selected location.",
        "I declare that I am aware of the fact that, even if a Nature Camp is allotted at selected location to me and or my team, the Camp could be canceled at any stage, by the Divisional Forest Officer/Wildlife Warden or any Competent Authority and I have no objection and I and or my team members and or my Head of Institution/Organisation will not raise any dispute against this.",
      ],
    };
  },

  watch: {
    dateKey() {
      this.checkDates();
    },
    leaderDetails2: {
      handler() {
        this.syncLeaderMembers();
      },
      deep: true,
    },
    "list.applicantCategory"(newVal, oldVal) {
      if (oldVal === undefined) return;
      if (newVal !== "Students") {
        this.list.studentCategory = "";
        this.list.instHeadName = "";
        this.list.headDesignation = "";
        this.list.idCardType = "";
        this.list.idNo = "";
        this.idProof = null;
        this.reqLetter = null;
        this.participantfile = null;
      }
    },
  },

  computed: {
    dateKey() {
      return (this.cdate || "") + "|" + ((this.list && this.list.campDuration) || "");
    },

    heroPhoto() {
      return campPhoto(this.campview || this.campidx || (this.campview && this.campview._id) || (this.campview && this.campview.campName), this.campview && this.campview.image);
    },
    heroFallback() {
      return campPhoto((this.campview && this.campview._id) || this.campidx || (this.campview && this.campview.campName));
    },

    isFreeCamp() {
      return (
        (this.campview && this.campview.campType === "free") ||
        !this.amounttobepaid ||
        Number(this.amounttobepaid) === 0
      );
    },

    feeLabel() {
      if (this.isFreeCamp) return "Free";
      if (!this.list || !this.list.campDuration || !this.amounttobepaid) return "Pick date & duration";
      return "\u20b9 " + Number(this.calculatedTotalAmount || 0).toLocaleString("en-IN");
    },

    endDate() {
      if (!this.cdate || !this.list || !this.list.campDuration) return null;
      const parts = String(this.cdate).split("-");
      if (parts.length !== 3) return null;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const dur = parseInt(this.list.campDuration, 10);
      if (isNaN(dur) || dur < 1) return null;

      const date = new Date(year, month, day);
      date.setDate(date.getDate() + dur - 1);

      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    formattedStartDate() {
      if (this.cdate) {
        const parts = String(this.cdate).split("-");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return this.cdate;
      }
      return "";
    },

    formattedEndDate() {
      if (this.endDate) {
        const [year, month, day] = this.endDate.split("-");
        return `${day}-${month}-${year}`;
      }
      return "";
    },

    calculatedTotalAmount() {
      const totalMembers = this.memberDetails2 ? this.memberDetails2.length : 0;
      const duration = this.list && this.list.campDuration ? Number(this.list.campDuration) : 1;
      return (this.amounttobepaid || 0) * totalMembers * duration;
    },

    calculatedGenderCounts2() {
      const counts = { male: 0, female: 0, transgender: 0 };
      if (this.memberDetails2) {
        this.memberDetails2.forEach((item) => {
          const selectedGender = item.memberGender;
          if (selectedGender === "Male") {
            counts.male++;
          } else if (selectedGender === "Female") {
            counts.female++;
          } else if (selectedGender === "Transgender") {
            counts.transgender++;
          }
        });
      }
      return counts;
    },

    formSteps() {
      const isStudent = this.list && this.list.applicantCategory === "Students";
      const steps = [
        {
          id: "bk-date",
          label: "Date & duration",
          done: !!(this.cdate && this.list && this.list.campDuration),
        },
        {
          id: "bk-applicant",
          label: "Applicant",
          done: !!(
            this.list &&
            this.list.applicantCategory &&
            (!isStudent || this.list.studentCategory) &&
            this.list.instName &&
            this.list.instAddress &&
            this.list.instMobileNo &&
            this.list.instEmail
          ),
        },
      ];
      if (isStudent) {
        steps.push({
          id: "bk-head",
          label: "Head of institution",
          done: !!(
            this.list &&
            this.list.instHeadName &&
            this.list.headDesignation &&
            this.list.idCardType &&
            this.list.idNo
          ),
        });
      }
      steps.push(
        {
          id: "bk-leaders",
          label: "Group leaders",
          done:
            this.leaderDetails2 &&
            this.leaderDetails2.length > 0 &&
            this.leaderDetails2.every(
              (l) => l.leaderName && l.leaderPhone && l.leaderEmail && l.leaderGender
            ),
        },
        {
          id: "bk-participants",
          label: "Participants",
          done:
            this.memberDetails2 &&
            this.memberDetails2.length > 0 &&
            this.memberDetails2.every(
              (m) => m.memberName && m.memberDob && m.memberGender
            ),
        },
        {
          id: "bk-docs",
          label: "Documents",
          done: !!(
            this.reqLetter &&
            this.participantfile &&
            this.leaderIdProof &&
            (!isStudent || this.idProof)
          ),
        },
        {
          id: "bk-declare",
          label: "Declaration",
          done: !!this.isChecked,
        }
      );
      return steps;
    },

    doneSteps() {
      return this.formSteps.filter((st) => st.done).length;
    },

    progress() {
      return Math.round((this.doneSteps / this.formSteps.length) * 100);
    },

    minDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const day = today.getDate();
      return `${year}-${month < 10 ? "0" : ""}${month}-${day < 10 ? "0" : ""}${day}`;
    },

    mobNumberRules() {
      return [
        (value) => !!value || "Mobile number is required",
        (value) => (value && /^[0-9]{10}$/.test(value)) || "Mobile number must be 10 digits",
      ];
    },

    PhoneRules() {
      return [
        (v) => !!v || "Phone number is required",
        (v) => /^[0-9]{10}$/.test(v) || "Phone number must be 10 digits",
      ];
    },

    aadharNumberRules() {
      return [
        (v) => !!v || "Aadhaar Number is required",
        (v) => /^[0-9]{12}$/.test(v) || "Aadhaar Number must be exactly 12 digits",
      ];
    },

    emailRules() {
      return [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ];
    },
  },

  mounted() {
    this.getList();
  },

  methods: {
    filterAllowedDates(val) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(val);
      const diffInDays = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));

      // Block past dates and the 7 days gap (day 0 through day 6)
      if (diffInDays < 7) return false;

      // Allow dates within advance booking window (up to 75 days in advance)
      if (diffInDays > 75) return false;

      // Blocked / already booked dates for this camp
      if (this.Reserveddates1 && this.Reserveddates1.length > 0) {
        return !this.Reserveddates1.includes(this.formatDate2(selectedDate));
      }

      return true;
    },

    formatDate2(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getReserveddates() {
      if (!this.campidx) return;
      axios({
        method: "POST",
        url: "/listblockdates/camp",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          campid: this.campidx,
        },
      })
        .then((response) => {
          this.Reserveddates1 = response.data.data || [];
        })
        .catch((error) => {
          console.error("Error fetching reserved dates:", error);
        });
    },

    autosaveFields() {
      const l = this.list || {};
      return {
        district: this.dist,
        ...this.autosaveDates(),
        applicantCategory: l.applicantCategory,
        studentCategory: l.studentCategory,
        applicantname: l.applicantname,
        instName: l.instName,
        instCode: l.instCode,
        instType: l.instType,
        instDistrict: l.instDistrict,
        instAddress: l.instAddress,
        instTeleNo: l.instTeleNo,
        instMobileNo: l.instMobileNo,
        instEmail: l.instEmail,
        instHeadName: l.instHeadName,
        headDesignation: l.headDesignation,
        idCardType: l.idCardType,
        idNo: l.idNo,
        leaderDetails: this.leaderDetails2 || [],
        memberDetails: this.memberDetails2 || [],
      };
    },

    // Only a date that passed the availability check is saved in the draft; a
    // cleared one is cleared there too. While a check is running the draft keeps
    // what it had (undefined fields aren't sent).
    autosaveDates() {
      const ok = this.availableDateKey === this.dateKey;
      const duration = this.list && this.list.campDuration;
      return {
        startDate: ok || !this.cdate ? this.cdate || "" : undefined,
        campDuration: ok || !duration ? duration || "" : undefined,
      };
    },

    autosaveFiles() {
      return {
        idProof: this.idProof,
        reqLetter: this.reqLetter,
        participantfile: this.participantfile,
        leaderIdProof: this.leaderIdProof,
      };
    },

    autosaveAppId() {
      return this.autosaveRouteId;
    },

    // Only drafts are autosaved; a submitted application is edited through the normal save.
    autosaveActive() {
      return !!(
        this.list &&
        this.list._id &&
        this.list.finalSubmit !== "true" &&
        this.list.editStatus !== "Disable"
      );
    },

    onAutosaved(data) {
      // Changing the dates after saving drops the reservation.
      if ("holdUntil" in data) this.setHold(data.holdUntil, data.holdInfo);
      if (data.holdMsg) {
        this.msg = data.holdMsg;
        this.showSnackBar = true;
      }
    },

    scrollToSection(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    filterNonNumericInput() {
      if (this.list.instHeadName) {
        this.list.instHeadName = this.list.instHeadName.replace(/[0-9]/g, "");
      }
      if (this.list.headDesignation) {
        this.list.headDesignation = this.list.headDesignation.replace(/[0-9]/g, "");
      }
    },

    validateInstCode() {
      if (this.list.instCode) {
        this.list.instCode = this.list.instCode.replace(/[^a-zA-Z0-9]/g, "");
      }
    },

    calculateMaxDate() {
      const currentDate = new Date();
      currentDate.setFullYear(currentDate.getFullYear() - 5);
      return currentDate.toISOString().split("T")[0];
    },

    formatDate(date) {
      if (!date) return "";
      const parts = String(date).split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return date;
    },

    hasFile(file) {
      return !!(file && (typeof file === "string" || file.name));
    },

    getFileName(file) {
      if (!file) return "No file chosen";
      if (typeof file === "string") return file;
      if (file.name) return file.name;
      return "Document uploaded";
    },

    getFileUrl(file) {
      if (typeof file === "string" && file) {
        return this.mediaURL + file;
      }
      return null;
    },

    paymentdialog() {
      this.submitdialog = false;
      this.submitted = false;
      this.paymentphonenumber = "";
      this.paymentname = "";
      this.paymentpancardnumber = "";
      this.paymentaddress = "";
      this.paymentpincode = "";
      this.paymenttaxid = "";
    },

    getPreview() {
      const token = localStorage.getItem("token");
      const downloadUrl = "/application/pdf";
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          appid: this.$route.query.id,
        },
        responseType: "blob",
      };

      axios
        .get(downloadUrl, config)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "preview.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          console.error("Error downloading preview:", error);
        });
    },

    submit2() {
      this.memberdialog = false;
    },

    handleFileChange(field) {
      const file = this.$refs[field].files[0];
      if (!file) return;

      if (!this.allowedFileTypes.includes(file.type)) {
        if (this.$appAlert) {
          this.$appAlert({
            type: "warning",
            title: "Unsupported file",
            message: "Only PDF and image files (JPEG, PNG) are allowed.",
          });
        } else {
          alert("Only PDF and image files (JPEG, PNG) are allowed.");
        }
        this.$refs[field].value = null;
        return;
      }

      const maxSize = 100 * 1024;
      if (file.size > maxSize) {
        if (this.$appAlert) {
          this.$appAlert({
            type: "warning",
            title: "File too large",
            message: "File size cannot exceed 100 KB.",
          });
        } else {
          alert("File size cannot exceed 100 KB.");
        }
        this.$refs[field].value = null;
        return;
      }
      this[field] = file;
    },

    validateFile(file) {
      return file && file.size <= 100 * 1024;
    },

    addLeaderDetails() {
      // Every leader is also a participant, so a leader counts towards the limit
      if (this.memberCapReached(this.memberDetails2.length)) {
        this.notifyMemberCap();
        return;
      }
      if (!this.leaderDetails2) this.leaderDetails2 = [];
      this.leaderDetails2.push({
        leaderName: "",
        leaderGender: "",
        leaderAddress1: "",
        leaderAddress2: "",
        telephone: "",
        leaderPhone: "",
        leaderEmail: "",
      });
    },

    removeLeaderDetail(index) {
      this.leaderDetails2.splice(index, 1);
    },

    removeLeader(index) {
      this.removeLeaderDetail(index);
    },

    removeleaderdetails(index) {
      this.removeLeaderDetail(index);
    },

    removeValues() {
      if (this.leaderDetails2 && this.leaderDetails2.length > 0) {
        this.leaderDetails2.pop();
      }
    },

    addValues() {
      if (this.memberCapReached(this.memberDetails2.length)) {
        this.notifyMemberCap();
        return;
      }
      if (!this.memberDetails2) this.memberDetails2 = [];
      this.memberDetails2.push({
        memberGender: "",
        memberDob: "",
        memberName: "",
        foodPreference: "",
        isFromLeader: false,
      });
    },

    removeMember(index) {
      // Group leaders are removed from the leader section, not from here.
      if (this.memberDetails2[index] && this.memberDetails2[index].isFromLeader) return;
      this.memberDetails2.splice(index, 1);
    },

    // Every group leader is also a participant (same as the apply form): the leader
    // rows come first, mirror the leader's name and gender, and only the date of
    // birth is entered in the participant list.
    syncLeaderMembers() {
      const leaders = this.leaderDetails2 || [];
      const members = this.memberDetails2 || [];
      const same = (a, b) =>
        String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
      // The server does not store which participant is a leader, so on a freshly
      // loaded application recognise them by position, name and gender.
      const flagged = members.some((m) => m.isFromLeader);
      const leaderMembers = leaders.map((leader, index) => {
        const current = members[index];
        const existing = flagged
          ? current && current.isFromLeader
          : current && same(current.memberName, leader.leaderName);
        return {
          memberName: leader.leaderName,
          memberGender: leader.leaderGender,
          memberDob: existing ? current.memberDob : "",
          foodPreference: existing ? current.foodPreference || "" : "",
          isFromLeader: true,
        };
      });
      const regularMembers = flagged
        ? members.filter((m) => !m.isFromLeader)
        : members.filter(
            (m, idx) => !(idx < leaders.length && same(m.memberName, leaders[idx].leaderName))
          );
      this.memberDetails2 = [...leaderMembers, ...regularMembers];
    },

    validateEmailleader(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    validateInstEmail() {
      return this.emailRules.every((rule) => rule(this.list.instEmail) === true);
    },

    isValidAadharNumber() {
      if (this.list.idCardType === "Aadhaar Card") {
        return /^[0-9]{12}$/.test(this.list.idNo);
      }
      return true;
    },

    // Same checks as the apply form. Returns the first problem found, or "".
    validateApplication() {
      const leaders = this.leaderDetails2 || [];
      for (let i = 0; i < leaders.length; i++) {
        if (!/^[a-zA-Z\s]*$/.test(leaders[i].leaderName || "")) {
          return "Leader Name contains invalid characters.";
        }
        if (!/^[a-zA-Z\s]*$/.test(leaders[i].leaderAddress1 || "")) {
          return "Leader Designation contains invalid characters.";
        }
        if (!/^[a-zA-Z0-9\s,.\-#()]*$/.test(leaders[i].leaderAddress2 || "")) {
          return "Leader Address contains invalid characters.";
        }
        if (!this.validateEmailleader(leaders[i].leaderEmail)) {
          return "Please enter a valid email address in Group Leader/Teaching Staff Details.";
        }
      }
      const l = this.list || {};
      if (!/^[a-zA-Z\s]*$/.test(l.instName || "")) {
        return "Institution Name contains invalid characters.";
      }
      if (!/^[a-zA-Z\s]*$/.test(l.instHeadName || "")) {
        return "Name of Head of Institution/Organisation contains invalid characters.";
      }
      if (!/^[a-zA-Z\s]*$/.test(l.headDesignation || "")) {
        return "Designation in Institute/Organisation contains invalid characters.";
      }
      if (!/^[a-zA-Z0-9\s,.\-#()]*$/.test(l.instAddress || "")) {
        return "Address of the institution contains invalid characters.";
      }
      if (!/^[0-9]{11}$/.test(l.instTeleNo || "")) {
        return "Institution Phone Number must be 11 digits.";
      }
      if (!this.validateInstEmail()) {
        return "Please enter a valid institution email address.";
      }
      if (!this.isValidAadharNumber()) {
        return "Aadhaar Number must be exactly 12 digits.";
      }
      const files = [
        [this.idProof, "ID Proof of Head"],
        [this.reqLetter, "Request Letter"],
        [this.participantfile, "Participant file"],
        [this.leaderIdProof, "Leader ID Proof"],
      ];
      for (const [file, label] of files) {
        // Only newly picked files have a size; saved ones are just a file name.
        if (file instanceof File && !this.validateFile(file)) {
          return `${label} file size exceeds 100 KB. Please select a smaller file.`;
        }
      }
      return "";
    },

    removeVehicle(index) {
      this.removeMember(index);
    },

    // Runs when the draft loads and whenever the date or duration changes. If
    // another applicant has the dates, clear them and say why.
    checkDates() {
      if (!this.cdate || !this.campidx || !this.autosaveActive()) return;
      const key = this.dateKey;
      axios({
        method: "POST",
        url: "/check/availability",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          startDate: this.cdate,
          campDuration: this.list.campDuration || undefined,
          campid: this.campidx,
          appid: this.$route.query.id,
        },
      })
        .then((response) => {
          if (key !== this.dateKey) return; // changed meanwhile
          if (response.data.status == true) {
            this.availableDateKey = key;
            this.autosave(); // starts the 10-minute hold now rather than on the next tick
          } else {
            this.dateMsg = response.data.msg;
            this.memberdialog = true;
            this.cdate = null;
            this.$set(this.list, "campDuration", null);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },

    editApplication() {
      if (!this.isChecked) {
        this.msg = "Please accept the undertaking before saving.";
        this.showSnackBar = true;
        return;
      }

      const problem = this.validateApplication();
      if (problem) {
        this.msg = problem;
        this.showSnackBar = true;
        return;
      }

      // Merge auxiliary arrays if any exist
      if (this.memberDetails && this.memberDetails.length > 0) {
        this.memberDetails.forEach((item) => this.memberDetails2.push(item));
        this.memberDetails = [];
      }
      if (this.leaderDetails && this.leaderDetails.length > 0) {
        this.leaderDetails.forEach((item) => this.leaderDetails2.push(item));
        this.leaderDetails = [];
      }

      const formData = new FormData();
      if (this.idProof instanceof File) {
        formData.append("idProof", this.idProof);
      }
      if (this.reqLetter instanceof File) {
        formData.append("reqLetter", this.reqLetter);
      }
      if (this.participantfile instanceof File) {
        formData.append("participantfile", this.participantfile);
      }
      if (this.leaderIdProof instanceof File) {
        formData.append("leaderIdProof", this.leaderIdProof);
      }

      formData.append("leaderDetails", JSON.stringify(this.leaderDetails2 || []));
      formData.append("memberDetails", JSON.stringify(this.memberDetails2 || []));
      formData.append("instHeadName", this.list.instHeadName || "");
      formData.append("headDesignation", this.list.headDesignation || "");
      formData.append("idCardType", this.list.idCardType || "");
      formData.append("idNo", this.list.idNo || "");
      formData.append("appid", this.$route.query.id);
      formData.append("instName", this.list.instName || "");
      formData.append("instCode", this.list.instCode || "");
      formData.append("instType", this.list.instType || "");
      formData.append("instAddress", this.list.instAddress || "");
      formData.append("instTeleNo", this.list.instTeleNo || "");
      formData.append("instMobileNo", this.list.instMobileNo || "");
      formData.append("instEmail", this.list.instEmail || "");
      if (this.list.instDistrict) {
        formData.append("instDistrict", this.list.instDistrict);
      }
      formData.append("givendate", this.cdate || "");
      formData.append("district", this.dist || "");
      formData.append("campDuration", this.list.campDuration || "");
      formData.append("memberCount", this.memberDetails2 ? this.memberDetails2.length : 0);
      formData.append("maleCount", this.calculatedGenderCounts2.male);
      formData.append("femaleCount", this.calculatedGenderCounts2.female);
      formData.append("transCount", this.calculatedGenderCounts2.transgender);
      formData.append("applicantCategory", this.list.applicantCategory || "");
      formData.append("studentCategory", this.list.studentCategory || "");
      formData.append("applicantname", this.list.applicantname || "");

      this.appLoading = true;
      axios({
        method: "POST",
        url: "/edit/application",
        headers: {
          "Content-Type": "multipart/form-data",
          token: localStorage.getItem("token"),
        },
        data: formData,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            // The save always goes through; say so when no reservation could be started.
            this.msg = response.data.holdMsg || response.data.msg || "Application updated successfully.";
            this.showSnackBar = true;
            this.isSaved = true;
            this.getList();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    submit() {
      this.submitted = true;
      axios({
        method: "POST",
        url: "/final/submit",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          appid: this.$route.query.id,
        },
      })
        .then((response) => {
          this.submitted = false;
          if (response.data.status == true) {
            this.list.finalSubmit = "true";
            this.setHold(null);
            const appNo = (String(response.data.msg || "").match(/\(([^)]+)\)/) || [])[1];
            this.$appAlert({
              type: "success",
              title: "Application submitted!",
              highlight: appNo,
              message: response.data.msg,
              confirmText: "View my applications",
            }).then(() => {
              this.$router.push({ path: "/SubmittedApplications" });
            });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.submitted = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    openFormInNewWindow() {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "https://etreasury.kerala.gov.in/api/eTreasury/service/ChallanGeneration.php";
      form.target = "_blank";

      const inputDataField = document.createElement("input");
      inputDataField.type = "hidden";
      inputDataField.name = "input_data";
      inputDataField.value = JSON.stringify({
        HMAC: this.getdetailsdata.HMAC,
        DATA: this.getdetailsdata.DATA,
      });

      const inputHeadersField = document.createElement("input");
      inputHeadersField.type = "hidden";
      inputHeadersField.name = "input_headers";
      inputHeadersField.value = JSON.stringify({
        clientId: this.getdetailsdata.clientId,
        authToken: this.getdetailsdata.authToken,
      });

      form.appendChild(inputDataField);
      form.appendChild(inputHeadersField);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    getList() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/single/application",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          appid: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.list = response.data.data || {};
            if (this.list.studentCategory) {
              const legacyCategoryMap = {
                "UP students": "Upper Primary",
                "HS students": "High School",
                "HSS students": "Higher Secondary",
                "College students": "College/University",
              };
              if (legacyCategoryMap[this.list.studentCategory]) {
                this.list.studentCategory = legacyCategoryMap[this.list.studentCategory];
              }
              if (!this.studentCategories.includes(this.list.studentCategory)) {
                this.studentCategories.push(this.list.studentCategory);
              }
            }
            this.campidx = this.list.campid ? (this.list.campid._id || this.list.campid) : null;
            this.campviewdata();
            this.getReserveddates();
            if (this.list.campDuration) {
              this.list.campDuration = String(this.list.campDuration);
            }
            if (this.list.memberDetails && Array.isArray(this.list.memberDetails)) {
              this.list.memberDetails.forEach((member) => {
                if (member.memberDob) {
                  member.memberDob = moment(member.memberDob).format("YYYY-MM-DD");
                }
              });
            }
            this.leaderDetails2 = this.list.leaderDetails || [];
            this.memberDetails2 = this.list.memberDetails || [];
            this.idProof = this.list.idProof || null;
            this.reqLetter = this.list.reqLetter || null;
            this.participantfile = this.list.participantfile || null;
            this.leaderIdProof = this.list.leaderIdProof || null;
            this.cdate = this.list.startDate ? moment(this.list.startDate).format("YYYY-MM-DD") : null;
            if (!this.dist && this.list.district) {
              this.dist = this.list.district;
            }
            this.availableDateKey = this.dateKey;
            this.setHold(
              this.list.finalSubmit !== "true" ? this.list.holdUntil : null,
              response.data.holdInfo
            );
            this.autosaveMarkSaved();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    campviewdata() {
      if (!this.campidx) return;
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/view/camp",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          campid: this.campidx,
        },
      })
        .then((response) => {
          this.campview = response.data.data || {};
          this.amounttobepaid = this.campview.rate || 0;
          this.appLoading = false;
        })
        .catch((err) => {
          this.ServerError = true;
          this.appLoading = false;
          console.error(err);
        });
    },
  },
};
</script>

<style>
.no-spinners {
  appearance: textfield;
  -moz-appearance: textfield;
  -webkit-appearance: textfield;
}

.inputPrice input::-webkit-outer-spin-button,
.inputPrice input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.form-label-text {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  display: inline-block;
  margin-bottom: 4px;
}

.required-star {
  color: #dc2626 !important;
  font-weight: 700;
  font-size: 14px;
  margin-left: 2px;
}

/* ================= Apply & Edit page (bk-*) ================= */
.bk-page {
  padding-bottom: 32px;
}

/* ---------- Camp header ---------- */
.bk-hero {
  position: relative;
  overflow: hidden;
  min-height: 210px;
  margin-bottom: 16px;
  border-radius: 22px;
  background: #0c4f4c;
  box-shadow: 0 16px 40px rgba(12, 79, 76, 0.22);
}

.bk-hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bk-hero-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(8, 51, 49, 0.94) 0%, rgba(12, 79, 76, 0.78) 50%, rgba(12, 79, 76, 0.2) 100%);
}

.bk-hero-content {
  position: relative;
  max-width: 720px;
  padding: 22px 32px 28px;
  color: #ffffff;
}

.bk-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 14px;
  padding: 5px 12px 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bk-back:hover {
  background: rgba(255, 255, 255, 0.22);
}

.bk-back:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.bk-hero-eyebrow {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #bbf7d0;
}

.bk-hero-title {
  margin: 6px 0 14px;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.5px;
  text-transform: capitalize;
  word-break: break-word;
}

.bk-hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bk-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  font-size: 13px;
  font-weight: 700;
}

.bk-tag--free {
  background: #dcfce7;
  border-color: #dcfce7;
  color: #15803d;
}

.bk-tag--paid {
  background: #fef3c7;
  border-color: #fef3c7;
  color: #b45309;
}

.bk-tag--veg {
  background: rgba(220, 252, 231, 0.18);
  border-color: rgba(187, 247, 208, 0.45);
  color: #ffffff;
}

/* ---------- Section progress ---------- */
.bk-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  padding: 10px;
  overflow-x: auto;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}

.bk-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 6px 12px 6px 6px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bk-step:hover {
  background: #f1f5f9;
}

.bk-step:focus-visible {
  outline: 2px solid #13736f;
  outline-offset: 1px;
}

.bk-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  font-size: 12.5px;
  font-weight: 800;
}

.bk-step--done {
  color: #0c4f4c;
}

.bk-step--done .bk-step-num {
  background: #16a34a;
  color: #ffffff;
}

/* ---------- Layout ---------- */
.bk-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 20px;
  align-items: start;
}

.bk-main {
  min-width: 0;
}

/* ---------- Sections ---------- */
.bk-section {
  margin-bottom: 18px;
  padding: 20px 20px 16px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  scroll-margin-top: 90px;
}

.bk-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef2f4;
}

.bk-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(19, 115, 111, 0.14), rgba(30, 142, 62, 0.14));
}

.bk-section-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.bk-section-title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
  color: #0f172a;
}

.bk-section-sub {
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
}

.v-btn.bk-head-action {
  flex-shrink: 0;
}

.bk-head-note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 12px;
  font-weight: 800;
}

/* Repeating leader / participant blocks */
.bk-repeater {
  border-radius: 16px;
  border: 1px solid #e3eeec;
  background: #f8fbfb;
  transition: border-color 0.15s ease;
}

.bk-repeater:hover {
  border-color: rgba(19, 115, 111, 0.35);
}

.bk-repeater-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding: 0 8px 10px;
  border-bottom: 1px solid #e8efee;
}

.bk-repeater-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #0c4f4c;
}

.bk-repeater-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #13736f;
  color: #ffffff;
  font-size: 12px;
}

/* Participant summary */
.bk-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.bk-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid transparent;
}

.bk-stat--total {
  background: #ecf6f5;
  border-color: #cfe7e4;
}

.bk-stat--male {
  background: #eff6ff;
  border-color: #dbeafe;
}

.bk-stat--female {
  background: #fdf2f8;
  border-color: #fce7f3;
}

.bk-stat--trans {
  background: #f5f3ff;
  border-color: #ede9fe;
}

.bk-stat-value {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  color: #0f172a;
}

.bk-stat-label {
  font-size: 12.5px;
  font-weight: 700;
  color: #64748b;
}

/* File upload boxes */
.bk-file {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1.5px dashed #b9cfcc;
  background: #f8fbfb;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.bk-file:hover,
.bk-file:focus-within {
  border-color: #13736f;
  background: #f0f8f7;
}

.bk-file:focus-within {
  outline: 2px solid rgba(19, 115, 111, 0.35);
  outline-offset: 2px;
}

.bk-file--done {
  border-style: solid;
  border-color: #86efac;
  background: #f0fdf4;
}

.bk-file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e3eeec;
}

.bk-file-icon .v-icon {
  color: #13736f !important;
}

.bk-file--done .bk-file-icon .v-icon {
  color: #16a34a !important;
}

.bk-file-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.bk-file-title {
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.35;
  color: #1e293b;
}

.bk-file-name {
  margin-top: 2px;
  font-size: 12.5px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bk-file--done .bk-file-name {
  color: #15803d;
  font-weight: 700;
}

.bk-file-view-btn {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 10px;
  background: #ffffff;
  color: #13736f !important;
  border: 1px solid #13736f;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s ease;
}

.bk-file-view-btn:hover {
  background: #ecf6f5;
}

.bk-file-btn {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 10px;
  background: #13736f;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 800;
}

.bk-file--done .bk-file-btn {
  background: #ffffff;
  color: #15803d;
  border: 1px solid #86efac;
}

.bk-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

/* Declaration */
.bk-terms {
  max-height: 260px;
  overflow-y: auto;
  padding: 16px !important;
  border-radius: 14px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}

.bk-consent {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 14px;
  background: #ecfaf8;
  border: 1px solid #99d5cf;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.bk-consent--on {
  background: #ecfaf8;
  border-color: #13736f;
}

.bk-consent-text {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  color: #134e4a;
}

.bk-consent--on .bk-consent-text {
  color: #134e4a;
}

/* View a chosen document (button; the link style is above) */
button.bk-file-view-btn {
  font-family: inherit;
  cursor: pointer;
}

/* Participant limit note */
.bk-limit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #ecf6f5;
  border: 1px solid #cfe7e4;
  font-size: 13px;
  line-height: 1.45;
  color: #134e4a;
}

.bk-limit--extra {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.bk-limit--full {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.bk-extra-tag {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Rows of fields share the full width, so a short last row leaves no gap */
.bk-section .layout.wrap > .flex.sm4,
.bk-section .layout.wrap > .flex.sm6,
.bk-section .layout.wrap > .flex.md3,
.bk-section .layout.wrap > .flex.md4 {
  flex: 1 1 220px;
  max-width: none;
}

/* Action bar: stays in view at the bottom so Save / Submit never need a scroll */
.bk-actions {
  position: sticky;
  bottom: 14px;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
}

.bk-actions-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: #475569;
}

.bk-actions-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.bk-actions-buttons .v-btn.btn-act {
  min-width: 120px !important;
}

/* ---------- Summary ---------- */
.bk-aside {
  position: sticky;
  top: 84px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.bk-summary {
  padding: 18px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
}

.bk-summary-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.bk-progress {
  height: 8px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.bk-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #13736f, #16a34a);
  transition: width 0.3s ease;
}

.bk-hold {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
  color: #78350f;
  font-size: 13px;
  line-height: 1.5;
}

.bk-hold--expired {
  border-color: #fcd34d;
  background: #fffbeb;
  color: #78350f;
}

.bk-hold-count {
  margin-top: 2px;
  font-size: 12px;
  opacity: 0.8;
}

.bk-autosave-note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11.5px;
  color: #13736f;
}

.bk-progress-text {
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
}

.bk-summary-list {
  margin: 14px 0 0;
}

/* Counts and applicant details in the summary */
.bk-sum-counts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 14px;
}

.bk-sum-count {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid transparent;
}

.bk-sum-count--total {
  grid-column: 1 / -1;
  background: #ecf6f5;
  border-color: #cfe7e4;
}

.bk-sum-count--leader {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.bk-sum-count--male {
  background: #eff6ff;
  border-color: #dbeafe;
}

.bk-sum-count--female {
  background: #fdf2f8;
  border-color: #fce7f3;
}

.bk-sum-count--trans {
  background: #f5f3ff;
  border-color: #ede9fe;
}

.bk-sum-count-value {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
  color: #0f172a;
}

.bk-sum-count-value small {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.bk-sum-count-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.bk-summary-list dd {
  min-width: 0;
  word-break: break-word;
}

.bk-summary-list > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-top: 1px dashed #e2e8f0;
  font-size: 13.5px;
}

.bk-summary-list dt {
  color: #64748b;
  font-weight: 600;
}

.bk-summary-list dd {
  margin: 0;
  text-align: right;
  font-weight: 800;
  color: #0f172a;
}

.bk-fee {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13.5px;
  font-weight: 700;
}

.bk-fee strong {
  font-size: 16px;
  color: #b45309;
  text-align: right;
}

.bk-fee--free {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.bk-fee--free strong {
  color: #15803d;
}

.bk-fee-note {
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #64748b;
}

@media (prefers-reduced-motion: reduce) {
  .bk-progress-bar,
  .bk-file,
  .bk-step {
    transition: none;
  }
}

/* ---------- Tablet ---------- */
@media (max-width: 1100px) {
  .bk-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .bk-aside {
    position: static;
    order: -1;
    max-height: none;
    overflow: visible;
  }

  .bk-summary-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 18px;
  }

  .bk-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ---------- Phones ---------- */
@media (max-width: 600px) {
  .bk-hero {
    min-height: 0;
    border-radius: 16px;
  }

  .bk-hero-content {
    padding: 16px 18px 20px;
  }

  .bk-hero-title {
    font-size: 23px;
  }

  .bk-section {
    padding: 16px 12px 12px;
    border-radius: 16px;
  }

  .bk-section-head {
    flex-wrap: wrap;
  }

  .bk-section-icon {
    width: 38px;
    height: 38px;
  }

  .bk-summary-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .bk-actions {
    padding: 12px;
  }

  .bk-actions-buttons,
  .bk-actions-buttons .v-btn.btn-act {
    width: 100%;
  }
}
@media (max-width: 960px) {
  .bk-actions {
    bottom: calc(72px + env(safe-area-inset-bottom));
  }
}
</style>